import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { PropsWithChildren } from 'react';
import { SurfaceColors } from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

interface PlaceholderShapeProps {
  level?: '0' | '1' | '2' | '3' | '4';
  rounded?: boolean;
  roundedTopLeft?: boolean;
  roundedTopRight?: boolean;
  roundedBottomLeft?: boolean;
  roundedBottomRight?: boolean;
  animated?: boolean;
  [x: string]: any;
}

const getDarkTheme = (muiTheme: any) => ({
  root: {
    '&.level-0': {
      background: hexToRGBA(SurfaceColors.dark, 1),
    },
    '&.level-1': {
      background: hexToRGBA(SurfaceColors.light, 0.5),
    },
    '&.level-2': {
      background: hexToRGBA(SurfaceColors.light, 0.2),
    },
    '&.level-3': {
      background: hexToRGBA(SurfaceColors.light, 0.1),
    },
    '&.level-4': {
      background: hexToRGBA(SurfaceColors.light, 0.3),
    },
    '&.animated': {
      animation: `$darkPlaceholderAnimation 1000ms ${muiTheme.transitions.easing.easeInOut} infinite`,
    },
  },
});

const getLightTheme = (muiTheme: any) => ({
  root: {
    '&.level-0': {
      background: hexToRGBA(SurfaceColors.light, 1),
    },
    '&.level-1': {
      background: hexToRGBA(SurfaceColors.dark, 0.05),
    },
    '&.level-2': {
      background: hexToRGBA(SurfaceColors.dark, 0.07),
    },
    '&.level-3': {
      background: hexToRGBA(SurfaceColors.dark, 0.1),
    },
    '&.level-4': {
      background: hexToRGBA(SurfaceColors.dark, 0.2),
    },
    '&.animated': {
      animation: `$lightPlaceholderAnimation 1000ms ${muiTheme.transitions.easing.easeInOut} infinite`,
    },
  },
});

const usePlaceholderShapeStyles = makeStyles((muiTheme) => ({
  root: {
    '&.dark-theme': getDarkTheme(muiTheme).root,
    '&.light-theme': getLightTheme(muiTheme).root,
    '&.rounded': {
      borderRadius: '4px',
    },
    '&.rounded-top-left': {
      borderTopLeftRadius: '4px',
    },
    '&.rounded-top-right': {
      borderTopRightRadius: '4px',
    },
    '&.rounded-bottom-left': {
      borderBottomLeftRadius: '4px',
    },
    '&.rounded-bottom-right': {
      borderBottomRightRadius: '4px',
    },
  },
  '@keyframes lightPlaceholderAnimation': {
    '0%, 100%': {
      opacity: 0.75,
    },
    '50%': {
      opacity: 1,
    },
  },
  '@keyframes darkPlaceholderAnimation': {
    '0%, 100%': {
      opacity: 0.25,
    },
    '50%': {
      opacity: 0.5,
    },
  },
}));

export const PlaceholderShape = ({
  children,
  theme,
  className,
  level = '1',
  rounded = false,
  roundedTopLeft = false,
  roundedTopRight = false,
  roundedBottomLeft = false,
  roundedBottomRight = false,
  animated = false,
  ...restProps
}: PropsWithTheme<PropsWithChildren<PlaceholderShapeProps>>) => {
  const classes = usePlaceholderShapeStyles();
  return (
    <div
      className={clsx(
        classes.root,
        `${theme}-theme`,
        className,
        `level-${level}`,
        rounded && 'rounded',
        roundedTopLeft && 'rounded-top-left',
        roundedTopRight && 'rounded-top-right',
        roundedBottomLeft && 'rounded-bottom-left',
        roundedBottomRight && 'rounded-bottom-right',
        animated && 'animated',
      )}
      {...restProps}
    >
      {children}
    </div>
  );
};
